import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Truck, PackageCheck, MapPin, Loader2, PartyPopper, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { confetti } from "@/lib/celebrate";

const STEPS = [["address", "Adresse"], ["preparing", "Wird gepackt"], ["shipped", "Unterwegs"], ["delivered", "Angekommen"]];
const fmt = (d) => new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

export const useMysteryBox = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/mystery-box").then(({ data }) => setD(data)).catch(() => setD({ supporter: false, boxes: [] })), []);
  useEffect(() => { load(); }, [load]);
  return [d, load];
};

const BoxArt = ({ shake }) => (
  <motion.div animate={shake ? { rotate: [0, -6, 6, -4, 4, 0], y: [0, -4, 0] } : {}} transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 2.4 }}
    className="relative w-20 h-20 shrink-0 rounded-2xl vip-gold-metal flex items-center justify-center shadow-lg shadow-amber-900/30">
    <Gift className="w-10 h-10" /><span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-rose-500 text-white text-sm font-extrabold flex items-center justify-center">?</span>
  </motion.div>
);

function Countdown({ d }) {
  const pct = Math.round((d.day_in_cycle / d.cycle) * 100);
  return (
    <div className="space-y-2" data-testid="mystery-countdown">
      <p className="text-[#efdcae] text-sm"><b className="text-[#f6e3a6] text-2xl font-extrabold" data-testid="mystery-days-left">Noch {d.days_left} {d.days_left === 1 ? "Tag" : "Tage"}</b> bis zu deiner nächsten Mystery-Box</p>
      <div className="h-3 rounded-full bg-white/10 overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.2, ease: "easeOut" }} className="h-full rounded-full vip-gold-metal" /></div>
      <p className="text-xs text-[#cdb88f]">Tag {d.day_in_cycle} von {d.cycle} · seit {d.streak_days} Tagen am Stück Unterstützer</p>
      {d.ends_before_next && <p className="text-xs text-amber-300" data-testid="mystery-ends-warning">Achtung: Dein Status endet am {fmt(d.ends_before_next)} – verlängere rechtzeitig, sonst beginnen die 30 Tage von vorn.</p>}
    </div>
  );
}

function AddressForm({ box, initial, onDone }) {
  const [a, setA] = useState({ full_name: "", street: "", zip: "", city: "", country: "Deutschland", ...initial, ...(box.address || {}) });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/mystery-box/${box.id}/address`, a); toast.success("Adresse gespeichert – wir packen deine Box! 🎁"); onDone(); }
    catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  const F = (k, ph, cls = "") => <input required value={a[k] || ""} onChange={(e) => setA({ ...a, [k]: e.target.value })} placeholder={ph} data-testid={`mystery-address-${k}`}
    className={`h-11 px-4 rounded-xl bg-white/95 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 ${cls}`} />;
  return (
    <form onSubmit={save} className="grid grid-cols-3 gap-2" data-testid="mystery-address-form">
      {F("full_name", "Vor- und Nachname", "col-span-3")}{F("street", "Straße und Hausnummer", "col-span-3")}{F("zip", "PLZ")}{F("city", "Ort", "col-span-2")}{F("country", "Land", "col-span-3")}
      <button disabled={busy} data-testid="mystery-address-save" className="col-span-3 h-11 rounded-full vip-gold-metal font-extrabold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2"><MapPin className="w-4 h-4" /> {box.status === "address" ? "Adresse bestätigen" : "Adresse ändern"}</button>
    </form>
  );
}

function BoxRow({ box, address, onChange }) {
  const [edit, setEdit] = useState(false);
  const idx = STEPS.findIndex(([s]) => s === box.status);
  const arrived = async (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    try { await api.post(`/mystery-box/${box.id}/delivered`); confetti({ x: r.left + r.width / 2, y: r.top, count: 120, spread: 1.6 }); toast.success("Viel Spaß beim Auspacken! 🎉"); onChange(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <li className="rounded-2xl p-4 bg-white/[0.05] border border-[#c99a3c]/30 space-y-3" data-testid={`mystery-box-${box.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-bold text-[#f6e3a6]">Mystery-Box #{box.n}</p>
        <span className="text-[11px] text-[#cdb88f]">freigeschaltet am {fmt(box.earned_at)}</span>
      </div>
      {box.status !== "forfeited" && <ol className="grid grid-cols-4 gap-1" data-testid={`mystery-steps-${box.id}`}>
        {STEPS.map(([s, l], i) => (
          <li key={s} className="space-y-1"><span className={`block h-1.5 rounded-full ${i <= idx ? "vip-gold-metal" : "bg-white/10"}`} /><span className={`block text-[10px] font-bold ${i <= idx ? "text-[#f6e3a6]" : "text-[#cdb88f]/60"}`}>{l}</span></li>
        ))}
      </ol>}
      {box.status === "forfeited" && <p className="text-sm text-[#cdb88f]" data-testid={`mystery-forfeited-${box.id}`}>Verfallen – dein Unterstützer-Status ist vor dem Versand geendet. Mit neuem Status beginnen die 30 Tage von vorn.</p>}
      {(box.status === "address" || edit) && <AddressForm box={box} initial={address} onDone={() => { setEdit(false); onChange(); }} />}
      {box.status === "preparing" && !edit && <p className="text-sm text-[#efdcae] flex items-center gap-2"><Gift className="w-4 h-4 shrink-0" /> Saskia & Stefan packen deine Box. <button onClick={() => setEdit(true)} data-testid={`mystery-edit-${box.id}`} className="underline text-[#f6e3a6]">Adresse ändern</button></p>}
      {box.status === "shipped" && (
        <div className="space-y-2">
          <p className="text-sm text-[#efdcae] flex items-center gap-2"><Truck className="w-4 h-4 shrink-0" /> Deine Box ist unterwegs!{box.tracking && <span data-testid={`mystery-tracking-${box.id}`}>Sendungsnummer: <b>{box.tracking}</b></span>}</p>
          <button onClick={arrived} data-testid={`mystery-arrived-${box.id}`} className="w-full h-12 rounded-full vip-gold-metal font-extrabold inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"><PackageCheck className="w-5 h-5" /> Angekommen – ich packe aus!</button>
        </div>
      )}
      {box.status === "delivered" && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-[#efdcae]" data-testid={`mystery-delivered-${box.id}`}>
          <PartyPopper className="w-4 h-4" /> Ausgepackt! Zeig der Community, was drin war:
          <Link to="/feed?compose=unboxing" data-testid={`mystery-share-${box.id}`} className="px-4 h-9 inline-flex items-center rounded-full bg-white text-rose-600 font-bold text-xs">Unboxing-Foto posten</Link>
        </div>
      )}
    </li>
  );
}

export function MysteryBoxCard() {
  const [d, load] = useMysteryBox();
  useEffect(() => { if (d && window.location.hash === "#mystery-box") setTimeout(() => document.getElementById("mystery-box")?.scrollIntoView({ behavior: "smooth" }), 150); }, [d]);
  if (!d) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  const pending = d.boxes.some((b) => b.status === "address" || b.status === "shipped");
  return (
    <section id="mystery-box" className="relative overflow-hidden rounded-[2rem] vip-lux p-6 sm:p-8 border border-[#c99a3c]/50 space-y-5 scroll-mt-28 fade-up" data-testid="mystery-box-card">
      <span className="vip-lux-pattern" aria-hidden />
      <div className="relative flex items-center gap-4">
        <BoxArt shake={pending || !d.supporter} />
        <div className="min-w-0">
          <p className="text-xs font-extrabold tracking-[0.3em] uppercase text-[#e6c56f]">Nur für Unterstützer</p>
          <h2 className="font-display text-2xl font-extrabold vip-gold-title">Die monatliche Mystery-Box</h2>
          <p className="text-sm text-[#cdb88f] mt-1">Alle 30 Tage am Stück als Projekt Unterstützer kommt ein echtes Überraschungspaket zu dir nach Hause – mit Lieblingssachen von uns, Merch, tollen Produkten und einem handgeschriebenen Brief von uns.</p>
        </div>
      </div>
      <ul className="relative text-xs text-[#efdcae] space-y-1 rounded-2xl bg-white/[0.04] border border-[#c99a3c]/25 p-3" data-testid="mystery-rules">
        <li>✦ Die Box gibt es für jeweils <b>30 Tage am Stück</b> mit aktivem Status „Projekt Unterstützer“.</li>
        <li>✦ Endet dein Status, <b>verfällt der Anspruch</b> auf noch nicht versendete Boxen.</li>
        <li>✦ Aktivierst du den Status neu, <b>beginnen die 30 Tage von vorn</b>.</li>
      </ul>
      <div className="relative">
        {d.supporter ? <Countdown d={d} /> : (
          <p className="text-sm text-[#efdcae] flex items-center gap-2" data-testid="mystery-locked"><Lock className="w-4 h-4 shrink-0" /> Werde Projekt Unterstützer und halte deinen Status 30 Tage am Stück – dann ist deine erste Box freigeschaltet.</p>
        )}
      </div>
      {d.boxes.length > 0 && <ul className="relative space-y-3">{d.boxes.map((b) => <BoxRow key={b.id} box={b} address={d.address} onChange={load} />)}</ul>}
    </section>
  );
}

export function MysteryPromoFeedCard() {
  const [s, setS] = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => { api.get("/mystery-box/feed-card").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s?.active || hidden || localStorage.getItem("mystery_promo_closed") === s.since) return null;
  const close = (e) => { e.preventDefault(); localStorage.setItem("mystery_promo_closed", s.since); setHidden(true); };
  return (
    <Link to="/community-unterstuetzen#mystery-box" data-testid="mystery-promo-feed-card" className="relative block overflow-hidden rounded-[2rem] vip-lux p-5 sm:p-6 border border-[#c99a3c]/50 space-y-4 hover:-translate-y-0.5 transition-transform fade-up">
      <span className="vip-lux-pattern" aria-hidden />
      <button onClick={close} aria-label="Schließen" data-testid="mystery-promo-feed-close" className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 text-[#f6e3a6] hover:bg-white/20 flex items-center justify-center"><X className="w-4 h-4" /></button>
      <div className="relative flex items-center gap-4 pr-6">
        <BoxArt shake />
        <div className="min-w-0">
          <p className="text-xs font-extrabold tracking-[0.3em] uppercase text-[#e6c56f]">Nur für Unterstützer</p>
          <p className="font-display text-xl sm:text-2xl font-extrabold vip-gold-title">Die monatliche Mystery-Box</p>
          <p className="text-sm text-[#cdb88f] mt-1">Alle 30 Tage am Stück als Projekt Unterstützer kommt ein echtes Überraschungspaket zu dir nach Hause – mit Lieblingssachen von uns, Merch, tollen Produkten und einem handgeschriebenen Brief von uns.</p>
        </div>
      </div>
      <ul className="relative text-xs text-[#efdcae] space-y-1 rounded-2xl bg-white/[0.04] border border-[#c99a3c]/25 p-3">
        <li>✦ Die Box gibt es für jeweils <b>30 Tage am Stück</b> mit aktivem Status „Projekt Unterstützer“.</li>
        <li>✦ Endet dein Status, <b>verfällt der Anspruch</b> auf noch nicht versendete Boxen.</li>
        <li>✦ Aktivierst du den Status neu, <b>beginnen die 30 Tage von vorn</b>.</li>
      </ul>
      <span className="relative inline-flex h-11 px-6 items-center rounded-full vip-gold-metal font-extrabold text-sm">Mehr erfahren</span>
    </Link>
  );
}

export function MysteryBoxFeedCard() {
  const [d] = useMysteryBox();
  const b = d?.boxes?.find((x) => x.status === "address" || x.status === "shipped");
  if (!b) return null;
  return (
    <Link to="/community-unterstuetzen#mystery-box" data-testid="mystery-feed-card" className="relative flex items-center gap-4 overflow-hidden rounded-3xl vip-lux p-5 border border-[#c99a3c]/50 hover:-translate-y-0.5 transition-transform">
      <span className="vip-lux-pattern" aria-hidden />
      <BoxArt shake />
      <span className="relative min-w-0">
        <span className="block text-[11px] font-extrabold uppercase tracking-[0.25em] text-[#e6c56f]">Deine Mystery-Box</span>
        <span className="block font-display text-lg font-extrabold vip-gold-title">{b.status === "address" ? "Freigeschaltet! Bestätige deine Lieferadresse" : "Ist unterwegs zu dir! 📦"}</span>
        <span className="block text-sm text-[#cdb88f]">{b.status === "address" ? "Tippe hier – dann packen wir deine Überraschung." : "Tippe auf „Angekommen“, sobald du sie hast."}</span>
      </span>
    </Link>
  );
}
